import { logInfo } from '@/utils/errorReporter';
import { useEffect, useRef } from 'react';
import { useMsalLogout } from './use-msal-logout';
import { useLocation } from '@tanstack/react-router';
import { useMsalAccount } from './use-msal-account';

const authLocations = ['/auth/chat', '/auth/video/call', '/auth/settings'];
const CHECK_INTERVAL_MS = 1000; // Check every 1s
const MAX_RETRIES = 5; // Maximum number of retry attempts before redirecting

export const useRedirectToAuthIfNoMsalAccount = () => {
  const { msalInstance, msalProgress } = useMsalAccount();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const logout = useMsalLogout('/error');
  const location = useLocation();

  const clearIntervalRef = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const clearRetryCountRef = () => {
    retryCountRef.current = 0;
  };

  useEffect(() => {
    // Clear any existing interval
    clearIntervalRef();

    // If we already have an account, no need to check
    const account = msalInstance.getActiveAccount();
    if (account) {
      clearRetryCountRef();
      return;
    }

    // If we're not on an auth location, no need to check
    if (!authLocations.includes(location.pathname)) {
      clearRetryCountRef();
      return;
    }

    // If MSAL is still processing (e.g., handling redirect), wait
    if (msalProgress !== 'none') {
      return;
    }

    // Start waiting for account to appear
    const checkForAccount = () => {
      const account = msalInstance.getActiveAccount();

      if (account) {
        // Account found, stop checking
        clearRetryCountRef();
        clearIntervalRef();
        return;
      }

      // Increment retry count
      retryCountRef.current += 1;

      if (retryCountRef.current < MAX_RETRIES) {
        // Retry: log and continue checking
        logInfo(
          `No active account found after attempt ${retryCountRef.current}/${MAX_RETRIES}, retrying...`,
          'use-redirect-to-auth-if-no-msal-account'
        );
        return;
      }

      // All retries exhausted, redirect to login
      logInfo(
        `No active account found after ${MAX_RETRIES} attempts, redirecting to login`,
        'use-redirect-to-auth-if-no-msal-account'
      );
      logInfo(`Current path: ${location.pathname}`, 'use-redirect-to-auth-if-no-msal-account');

      clearRetryCountRef();
      clearIntervalRef();
      logout();
    };

    // Start checking at regular intervals
    intervalRef.current = setInterval(checkForAccount, CHECK_INTERVAL_MS);

    // Cleanup
    return () => {
      clearIntervalRef();
      clearRetryCountRef();
    };
  }, [msalProgress, msalInstance, logout, location]);
};
